import {
  ApplicationsConfigsDto,
  ApplicationQuestionResponseV2Dto,
  CustomQuestionTypeEnum,
  CustomQuestionResponseTypeEnum,
  ApplicantEmploymentHistoryV2Dto,
  ReadDocumentDto,
  EmploymentStatusEnum,
  ApplicantV2Dto,
  ApplicantMinorV2Dto,
  ApplicantRentalHistoryV2Dto,
  ApplicantPetV2Dto,
  ApplicationVehicleDtoTypeEnum,
  ApplicantVehicleV2Dto,
  ApplicationAcceptedTsCsAuditV2Dto,
  TermsAndConditionsDto,
  ApplicationV2Dto,
  AddressDTO,
} from '@inosio/payments-sdk';

export interface ApplicationSectionProps {
  appUuid?: string;
  Validator: BaseApplicationItemValidator<{ Uuid: string }>;
}
export abstract class BaseApplicationItemValidator<T extends { Uuid: string }> {
  constructor(protected readonly applicationsConfigs: ApplicationsConfigsDto, protected readonly customQuestionResponses: ApplicationQuestionResponseV2Dto[]) {}

  protected checkObjectCustomQuestionsForCompleteness(entityUuid: string, customQuestionType: CustomQuestionTypeEnum) {
    let areAllRequiredQuestionsCompleted = true; //innocent until proven guilty.

    const customQuestions = (this.applicationsConfigs?.ApplicationsQuestions ?? []).filter((q) => q.CustomQuestionType === customQuestionType);
    const savedAnswers = this.customQuestionResponses?.filter((a) => a.BelongsToUuid === entityUuid);

    //original
    customQuestions
      ?.filter((q) => !q.ParentCustomQuestionUuid)
      .forEach((parentLevelQuestion) => {
        //iterate through parents

        const parentPossiblySavedAnswer = savedAnswers?.find((a) => a.QuestionUuid === parentLevelQuestion.Uuid);
        if (parentLevelQuestion.IsRequired && parentLevelQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.FreeText) {
          if (!parentPossiblySavedAnswer?.Response) {
            areAllRequiredQuestionsCompleted = false;
          }
        }

        if (parentLevelQuestion.IsRequired && parentLevelQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean) {
          if (!(parentPossiblySavedAnswer && parentPossiblySavedAnswer.Response !== undefined)) {
            areAllRequiredQuestionsCompleted = false;
          }
        }

        //we don't need to check the children if they answered "No" to a boolean type parent. That is, children are only required if the parent boolean is answered true true.
        if (
          parentLevelQuestion.IsRequired &&
          areAllRequiredQuestionsCompleted &&
          parentLevelQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean &&
          parentPossiblySavedAnswer?.Response !== undefined && // we don't really need this check, it's redundant
          Boolean(parentPossiblySavedAnswer?.Response) !== false
        ) {
          customQuestions
            ?.filter((cq) => cq.ParentCustomQuestionUuid === parentLevelQuestion.Uuid)
            .forEach((childQuestion) => {
              if (childQuestion.IsRequired && childQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.FreeText) {
                if (!savedAnswers?.find((a) => a.QuestionUuid === childQuestion.Uuid && a.Response)) {
                  areAllRequiredQuestionsCompleted = false;
                }
              }

              if (childQuestion.IsRequired && childQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean) {
                if (!savedAnswers?.find((a) => a.QuestionUuid === childQuestion.Uuid && a.Response !== undefined)) {
                  areAllRequiredQuestionsCompleted = false;
                }
              }
            });
        }
      });

    return areAllRequiredQuestionsCompleted;
  }

  protected abstract customQuestionType: CustomQuestionTypeEnum;

  /**
   * Validation function to determine if all required fields are correctly populated.   *
   * @param entityObject
   * @returns true if all good. false otherwise.
   */
  protected abstract IsEntityDetailsComplete(entityObject: T): boolean;

  protected IsAddressComplete(address: AddressDTO): boolean {
    return !!address && !!address.Line1 && !!address.City && !!address.State && !!address.Country && !!address.Zip;
  }

  public IsEntireObjectComplete(entityObject: T) {
    return this.IsEntityDetailsComplete(entityObject) && this.checkObjectCustomQuestionsForCompleteness(entityObject.Uuid, this.customQuestionType);
  }
}

export class EmploymentIncomeValidator extends BaseApplicationItemValidator<ApplicantEmploymentHistoryV2Dto> {
  /**
   *
   */
  constructor(protected applicationsConfigs: ApplicationsConfigsDto, protected customQuestionResponses: ApplicationQuestionResponseV2Dto[], private readonly allDocuments: ReadDocumentDto[]) {
    super(applicationsConfigs, customQuestionResponses);
  }

  protected IsEntityDetailsComplete(employment: ApplicantEmploymentHistoryV2Dto): boolean {
    let isEmploymentComplete = false;

    const {
      Uuid,
      Address,
      Status,
      Industry,
      Title,
      CompanyName,
      CompanyPhone,
      MonthlySalary,
      StartDate,
      EndDate,
      OtherIncomeAmount,
      CompanyContactName,
      CompanyContactPhone,
      CompanyContactEmail,
      CurrentlyEmployed,
    } = employment;

    const IncomeFiles = this.allDocuments?.filter((d) => d.documentTypeId === 4 && d.belongsToEntityUuid === Uuid);
    const hasEndDateOrIsCurrentlyEmployed = Boolean(EndDate) || Boolean(CurrentlyEmployed);
    if (StartDate && new Date(StartDate) > new Date()) isEmploymentComplete = false;
    if (EndDate && new Date(EndDate) > new Date()) isEmploymentComplete = false;
    if (StartDate && EndDate && new Date(StartDate) > new Date(EndDate)) isEmploymentComplete = false;

    if (Status === EmploymentStatusEnum.Employed) {
      if (
        this.IsAddressComplete(Address!) &&
        Title &&
        CompanyName &&
        CompanyPhone &&
        MonthlySalary &&
        StartDate &&
        hasEndDateOrIsCurrentlyEmployed &&
        (IncomeFiles?.length || !this.applicationsConfigs?.ApplicationsRequireEmploymentDocsUpload) &&
        CompanyContactName &&
        CompanyContactPhone &&
        CompanyContactEmail
      ) {
        isEmploymentComplete = true;
      }
    } else if (Status === EmploymentStatusEnum.SelfEmployed) {
      if (
        this.IsAddressComplete(Address!) &&
        Industry &&
        CompanyName &&
        StartDate &&
        hasEndDateOrIsCurrentlyEmployed &&
        MonthlySalary &&
        (IncomeFiles?.length || !this.applicationsConfigs?.ApplicationsRequireEmploymentDocsUpload)
      ) {
        isEmploymentComplete = true;
      }
    } else if (Status === EmploymentStatusEnum.NotEmployed) {
      if (OtherIncomeAmount || OtherIncomeAmount === 0) {
        //0 is allowed when not employed
        isEmploymentComplete = true;
      }
    }

    return isEmploymentComplete;
  }
  protected customQuestionType = CustomQuestionTypeEnum.EmployeeV1;
}

export class PersonalInfoValidator extends BaseApplicationItemValidator<ApplicantV2Dto> {
  constructor(protected applicationsConfigs: ApplicationsConfigsDto, protected customQuestionResponses: ApplicationQuestionResponseV2Dto[], private readonly allDocuments: ReadDocumentDto[]) {
    super(applicationsConfigs, customQuestionResponses);
  }

  protected customQuestionType: CustomQuestionTypeEnum = CustomQuestionTypeEnum.PersonalV1;
  protected IsEntityDetailsComplete(applicant: ApplicantV2Dto): boolean {
    let isPersonalInfoComplete = false;

    //check personal info status
    const { Uuid, FirstName, LastName, Dob, PhoneNumber, Ssn, GovIdType, GovIdNumber, HasNoSsn } = applicant;
    const IDFiles = this.allDocuments?.filter((d) => d.documentTypeId === 3 && d.belongsToEntityUuid === Uuid);
    isPersonalInfoComplete = !!(
      FirstName &&
      LastName &&
      Dob &&
      PhoneNumber &&
      (Ssn || HasNoSsn) &&
      GovIdType &&
      GovIdNumber &&
      (IDFiles?.length || !this.applicationsConfigs?.ApplicationsRequirePersonalDocsUpload)
    );

    return isPersonalInfoComplete;
  }
}

export class MinorOccupantValidator extends BaseApplicationItemValidator<ApplicantMinorV2Dto> {
  constructor(protected applicationsConfigs: ApplicationsConfigsDto, protected customQuestionResponses: ApplicationQuestionResponseV2Dto[]) {
    super(applicationsConfigs, customQuestionResponses);
  }

  protected customQuestionType: CustomQuestionTypeEnum = CustomQuestionTypeEnum.AdditionalOccupantV1;
  protected IsEntityDetailsComplete(minor: ApplicantMinorV2Dto): boolean {
    const ApplicationsCollectNonAdultOccupantDob = !!this.applicationsConfigs?.ApplicationsCollectNonAdultOccupantDob;
    const { FirstName, LastName, Dob } = minor;
    let isSectionComplete;
    if (ApplicationsCollectNonAdultOccupantDob) {
      isSectionComplete = !!(FirstName && LastName && Dob);
    } else {
      isSectionComplete = !!(FirstName && LastName);
    }
    return isSectionComplete;
  }
}

export class CoapplicantValidator extends BaseApplicationItemValidator<ApplicantV2Dto> {
  constructor(protected applicationsConfigs: ApplicationsConfigsDto, protected customQuestionResponses: ApplicationQuestionResponseV2Dto[]) {
    super(applicationsConfigs, customQuestionResponses);
  }

  protected customQuestionType: CustomQuestionTypeEnum = CustomQuestionTypeEnum.AdditionalOccupantV1;

  //for coapplicants, there really are no custom questions or anything, so we can acctually override that guy too. Special case
  protected checkObjectCustomQuestionsForCompleteness() {
    //we may not need to do this after all. Maybe let it run naturally with zero results.
    return true;
  }

  protected IsEntityDetailsComplete(coapplicant: ApplicantV2Dto): boolean {
    //for this one, we are trusting the DB status and that's that.
    return coapplicant.Status === 'COMPLETED';
  }
}

export class AdditionalQuestionsValidator extends BaseApplicationItemValidator<ApplicantV2Dto> {
  constructor(protected applicationsConfigs: ApplicationsConfigsDto, protected customQuestionResponses: ApplicationQuestionResponseV2Dto[]) {
    super(applicationsConfigs, customQuestionResponses);
  }

  protected customQuestionType: CustomQuestionTypeEnum = CustomQuestionTypeEnum.CustomV1;
  protected IsEntityDetailsComplete(applicant: ApplicantV2Dto): boolean {
    return true; //there's no entity specific stuff for custom questions.
  }
}

export class RentalHistoryValidator extends BaseApplicationItemValidator<ApplicantRentalHistoryV2Dto> {
  /**
   *
   */
  constructor(protected applicationsConfigs: ApplicationsConfigsDto, protected customQuestionResponses: ApplicationQuestionResponseV2Dto[]) {
    super(applicationsConfigs, customQuestionResponses);
  }

  protected IsEntityDetailsComplete(rentalRecord: ApplicantRentalHistoryV2Dto): boolean {
    const { 
        Address, 
        ContactFacilityName, 
        ContactEmail, 
        ContactPhone, 
        MonthlyPayment, 
        MoveInDate, 
        MoveOutDate, 
        IsCurrentAddress, 
        PurchaseDate, 
        RentalHistoryTypeId 
    } = rentalRecord;

    const isFutureDate = (date: string | undefined) => date && new Date(date) > new Date();
    const isAddressComplete = this.IsAddressComplete(Address!);

    let isRentalHistoryRecordComplete = true;

    if (RentalHistoryTypeId === 3) {
        if (!isAddressComplete) isRentalHistoryRecordComplete = false;
        if (isFutureDate(MoveInDate) || isFutureDate(MoveOutDate) || (MoveInDate && MoveOutDate && new Date(MoveInDate) > new Date(MoveOutDate))) {
            isRentalHistoryRecordComplete = false;
        }
        if (!IsCurrentAddress && !MoveOutDate) isRentalHistoryRecordComplete = false;
    } else {
    
        if (!isAddressComplete || !MonthlyPayment || !MoveInDate) {
            isRentalHistoryRecordComplete = false;
        }
        if (isFutureDate(MoveInDate) || isFutureDate(MoveOutDate) || (MoveInDate && MoveOutDate && new Date(MoveInDate) > new Date(MoveOutDate))) {
            isRentalHistoryRecordComplete = false;
        }
        if (RentalHistoryTypeId === 2 && !PurchaseDate) isRentalHistoryRecordComplete = false;
        if (RentalHistoryTypeId !== 2 && (!ContactPhone || !ContactEmail || !ContactFacilityName)) {
            isRentalHistoryRecordComplete = false;
        }
        if (!IsCurrentAddress && !MoveOutDate) isRentalHistoryRecordComplete = false;
    }

    return isRentalHistoryRecordComplete;
}
  protected customQuestionType = CustomQuestionTypeEnum.RentalHistoryV1;
}

export class ApplicantPetValidator extends BaseApplicationItemValidator<ApplicantPetV2Dto> {
  constructor(protected applicationsConfigs: ApplicationsConfigsDto, protected customQuestionResponses: ApplicationQuestionResponseV2Dto[], private readonly allDocuments: ReadDocumentDto[]) {
    super(applicationsConfigs, customQuestionResponses);
  }

  protected customQuestionType: CustomQuestionTypeEnum = CustomQuestionTypeEnum.PetV1;
  protected IsEntityDetailsComplete(pet: ApplicantPetV2Dto): boolean {
    let isPetComplete = false;

    const { Species, Name, Breed, WeightLbs, LivesOnPremises, Uuid } = pet;
    const petFiles = this.allDocuments?.filter((d) => d.documentTypeId === 5 && d.belongsToEntityUuid === Uuid);
    isPetComplete = !!(Species && Name && Breed && WeightLbs && LivesOnPremises !== null && (petFiles?.length || !this.applicationsConfigs?.ApplicationsRequirePetsDocsUpload));

    return isPetComplete;
  }
}

export class ApplicantVehicleValidator extends BaseApplicationItemValidator<ApplicantVehicleV2Dto> {
  constructor(protected applicationsConfigs: ApplicationsConfigsDto, protected customQuestionResponses: ApplicationQuestionResponseV2Dto[]) {
    super(applicationsConfigs, customQuestionResponses);
  }

  protected customQuestionType: CustomQuestionTypeEnum = CustomQuestionTypeEnum.VehicleV1;
  protected IsEntityDetailsComplete(vehicle: ApplicantVehicleV2Dto): boolean {
    let isVehicleComplete = false;

    const { Type, Make, Model, Year, Color, LicensePlateNo, Length } = vehicle;
    const independentFieldsComplete = Type && Make && Model && Year && Color;

    // based on type of vehicle, our validation would differ
    if (Type === ApplicationVehicleDtoTypeEnum.Car || Type === ApplicationVehicleDtoTypeEnum.Truck || Type === ApplicationVehicleDtoTypeEnum.Suv) {
      isVehicleComplete = !!(independentFieldsComplete && LicensePlateNo);
    } else if (Type === ApplicationVehicleDtoTypeEnum.Rv || Type === ApplicationVehicleDtoTypeEnum.Trailer) {
      isVehicleComplete = !!(independentFieldsComplete && LicensePlateNo && Length);
    } else {
      isVehicleComplete = !!(independentFieldsComplete && Length);
    }

    return isVehicleComplete;
  }
}

export class PropertyInfoValidator extends BaseApplicationItemValidator<ApplicationV2Dto> {
  constructor(protected applicationsConfigs: ApplicationsConfigsDto, protected customQuestionResponses: ApplicationQuestionResponseV2Dto[]) {
    super(applicationsConfigs, customQuestionResponses);
  }
  protected customQuestionType: CustomQuestionTypeEnum = CustomQuestionTypeEnum.CustomV1; //doesnt really matter

  //for the property info section  there really are no custom questions or anything, so we can acctually override that guy too. Special case
  protected checkObjectCustomQuestionsForCompleteness() {
    return true;
  }

  protected IsEntityDetailsComplete(application: ApplicationV2Dto): boolean {
    let isApplicationEntityComplete = false;
    const { ShowingAgentCompany, ShowingAgentName, ShowingAgentContactDetails, HasSeenProperty, DesiredLeaseTerm, DesiredMoveInDate, ExpectedRentAmount } = application;
    const independentFieldsComplete = !!(DesiredLeaseTerm && DesiredMoveInDate && ExpectedRentAmount);
    //some fields are dependent on other fields
    if (HasSeenProperty === true) {
      isApplicationEntityComplete = !!(ShowingAgentCompany && ShowingAgentName && ShowingAgentContactDetails) && independentFieldsComplete;
    } else if (HasSeenProperty === false) {
      isApplicationEntityComplete = independentFieldsComplete;
    }
    return isApplicationEntityComplete;
  }
}

export type ApplicationConsentEntity = {
  Uuid: string; //this is an applicant uuid
  consentToCheck: TermsAndConditionsDto;
  acceptedTsCsAudit: ApplicationAcceptedTsCsAuditV2Dto[];
};

export class ReleasesConsentsValidator extends BaseApplicationItemValidator<ApplicationConsentEntity> {
  constructor(protected applicationsConfigs: ApplicationsConfigsDto, protected customQuestionResponses: ApplicationQuestionResponseV2Dto[]) {
    super(applicationsConfigs, customQuestionResponses);
  }

  protected customQuestionType: CustomQuestionTypeEnum = CustomQuestionTypeEnum.CustomV1; //doesnt really matter

  //for releases and consents section, there really are no custom questions or anything, so we can acctually override that guy too. Special case
  protected checkObjectCustomQuestionsForCompleteness() {
    return true;
  }

  protected IsEntityDetailsComplete(consentEntity: ApplicationConsentEntity): boolean {
    const acceptedTsCsAudit: ApplicationAcceptedTsCsAuditV2Dto[] = consentEntity.acceptedTsCsAudit;
    const consentToCheck = consentEntity.consentToCheck;
    const applicantUuid = consentEntity.Uuid;

    let isConsentAgreed = false;
    if (acceptedTsCsAudit && acceptedTsCsAudit.some((audit) => audit.ApplicantUuid === applicantUuid && audit.TsCsId === consentToCheck.Id && !!audit.AuditId)) {
      isConsentAgreed = true;
    }
    return isConsentAgreed;
  }
}
